import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, UserCheck, Loader2, Phone, MapPin, Zap, Bell, Shield } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { trackEvent } from '../../utils/tracking';

/**
 * Profile completion modal.
 *
 * Shown when an AUTHENTICATED user with an incomplete profile (no phone)
 * tries to book a flight/hotel/package. Per the original product spec
 * "force user profile creation before making bookings", we collect:
 *   • phone (required) — partners sometimes need to verify
 *   • country (optional) — improves analytics, default to existing
 *
 * On successful save we call `onComplete(updatedUser)` so the caller
 * (useBookingFlow) can resume the booking redirect that was paused.
 */
export default function ProfileCompletionModal({ isOpen, onClose, onComplete }) {
  const { t } = useLanguage();
  const { user, updateProfile } = useAuth();
  const [phone, setPhone] = useState(user?.phone || '');
  const [country, setCountry] = useState(user?.country || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    const digits = (phone || '').replace(/\D/g, '');
    if (digits.length < 4) {
      setError(t('profileGate.error.phone'));
      return;
    }
    setLoading(true);
    trackEvent('profile_gate_submit', { hasCountry: !!country });
    const res = await updateProfile({ phone: phone.trim(), country: country.trim() || null });
    setLoading(false);
    if (res.ok) {
      trackEvent('profile_gate_complete');
      onComplete?.(res.user);
    } else {
      setError(res.error || t('authGate.genericError'));
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[320] flex items-start sm:items-center justify-center bg-black/65 backdrop-blur-md p-4 pt-8 sm:pt-4 overflow-y-auto animate-in fade-in"
      role="dialog"
      aria-modal="true"
      data-testid="profile-gate-modal"
    >
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative animate-in zoom-in-95 my-auto">
        <button
          type="button"
          onClick={() => {
            trackEvent('profile_gate_dismiss');
            onClose?.();
          }}
          aria-label={t('common.close')}
          data-testid="profile-gate-close"
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-md">
          <UserCheck className="w-6 h-6 text-white" />
        </div>

        <h2 className="text-xl font-bold text-center text-gray-900 mb-1" data-testid="profile-gate-title">
          {t('profileGate.title')}
        </h2>
        <p className="text-sm text-center text-gray-500 mb-5 leading-relaxed">
          {t('profileGate.subtitle')}
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="profile-gate-form">
          <div>
            <label htmlFor="profile-gate-phone" className="block text-xs font-semibold text-gray-700 mb-1">
              {t('profileGate.phoneLabel')} *
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="profile-gate-phone"
                data-testid="profile-gate-phone-input"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t('profileGate.phonePlaceholder')}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          <div>
            <label htmlFor="profile-gate-country" className="block text-xs font-semibold text-gray-700 mb-1">
              {t('profileGate.countryLabel')}
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="profile-gate-country"
                data-testid="profile-gate-country-input"
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder={t('profileGate.countryPlaceholder')}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                autoComplete="country-name"
              />
            </div>
          </div>

          {error && (
            <p data-testid="profile-gate-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="profile-gate-submit"
            className="w-full px-4 py-2.5 min-h-[48px] bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-teal-700 disabled:opacity-60 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('profileGate.continueBtn')}
          </button>
        </form>

        <ul className="mt-5 grid grid-cols-1 gap-2 text-xs text-gray-500">
          <li className="flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-emerald-500" />
            <span>{t('profileGate.benefitFasterBooking')}</span>
          </li>
          <li className="flex items-center gap-2">
            <Bell className="w-3.5 h-3.5 text-emerald-500" />
            <span>{t('profileGate.benefitAlerts')}</span>
          </li>
          <li className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-emerald-500" />
            <span>{t('profileGate.benefitTrust')}</span>
          </li>
        </ul>
      </div>
    </div>,
    document.body
  );
}
